import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Download, ShieldAlert, RefreshCw, ChevronDown, ChevronRight, MapPin, Users, Ban,
  Eye, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import audienceExtractsApi, { blobErrorMessage } from '@/api/audienceExtracts';
import AreasTable from '@/components/AreasTable';

// Every count on this page comes from the server; nothing is summed here.
const fmtInt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-ZA'));
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('en-ZA');
};
const humanKey = (k) => String(k).replace(/_/g, ' ');

// The dashboard describes the scope it applied: "national", "Gauteng Metro",
// "3 offices", "none" (no region or office assigned).
const scopeLabel = (scope) => (scope === 'national' ? 'National' : scope);

const GROUPS = [
  {
    id: 'seed',
    title: 'Lookalike seeds',
    hint: 'Upload as the source of a lookalike audience. Do not target these people directly.',
    icon: Users,
  },
  {
    id: 'retarget',
    title: 'Retargeting',
    hint: 'Show ads to these people.',
    icon: RefreshCw,
  },
  {
    id: 'exclude',
    title: 'Exclusion',
    hint: 'Apply as an exclusion on every campaign.',
    icon: Ban,
  },
  {
    id: 'reserve',
    title: 'Reserve',
    hint: 'Kept in reserve. Use only when asked to.',
    icon: Users,
  },
];

function groupOf(a) {
  if (a.reserve) return 'reserve';
  return GROUPS.some((g) => g.id === a.use) ? a.use : 'reserve';
}

function AudienceCard({ audience, filters, reserve }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const download = async (platform) => {
    setBusy(platform);
    try {
      const { filename, rows } = await audienceExtractsApi.download(
        audience.key,
        platform,
        audience.filterable === false ? {} : filters,
      );
      toast.success(
        `Downloaded ${filename}${rows ? ` (${fmtInt(rows)} rows)` : ''}. Delete it once it is uploaded.`,
      );
    } catch (err) {
      toast.error(await blobErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card
      className={reserve ? 'opacity-70 border-dashed' : undefined}
      data-testid={`audience-${audience.key}`}
    >
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base flex items-center gap-2">
              {audience.name}
              {reserve && <Badge variant="outline">Reserve</Badge>}
              {audience.window && (
                <Badge variant="secondary" className="font-normal">{audience.window}</Badge>
              )}
            </CardTitle>
            {audience.description && (
              <CardDescription>{audience.description}</CardDescription>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              variant={reserve ? 'outline' : 'default'}
              disabled={busy !== null}
              onClick={() => download('meta')}
              data-testid={`download-meta-${audience.key}`}
            >
              <Download className="h-4 w-4 mr-1" />
              {busy === 'meta' ? 'Preparing…' : 'Download for Meta'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null}
              onClick={() => download('google')}
              data-testid={`download-google-${audience.key}`}
            >
              <Download className="h-4 w-4 mr-1" />
              {busy === 'google' ? 'Preparing…' : 'Download for Google'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="grid grid-cols-3 gap-4 text-sm max-w-md">
          <div>
            <dt className="text-muted-foreground">People</dt>
            <dd className="font-semibold tabular-nums" data-testid={`people-${audience.key}`}>
              {fmtInt(audience.people)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">With email</dt>
            <dd className="font-semibold tabular-nums">{fmtInt(audience.with_email)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">With mobile</dt>
            <dd className="font-semibold tabular-nums">{fmtInt(audience.with_mobile)}</dd>
          </div>
        </dl>
        {audience.small_seed && (
          <p
            className="flex gap-2 text-sm text-amber-800 dark:text-amber-300"
            data-testid={`small-seed-${audience.key}`}
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            Under 1,000 people: may be too small for a good lookalike — use a wider region or the
            national seed.
          </p>
        )}
        {audience.filterable === false && (
          <p className="text-sm text-muted-foreground" data-testid={`national-${audience.key}`}>
            Always national: the region and office filters do not apply to this list.
          </p>
        )}
        {audience.rules?.length > 0 && (
          <div>
            <button
              type="button"
              className="flex items-center gap-1 text-sm text-primary hover:underline"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
            >
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {open ? 'Hide the rules' : 'Who is in it'}
            </button>
            {open && (
              <ul className="mt-2 list-disc pl-6 text-sm space-y-1">
                {audience.rules.map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RebuildPanel({ status, onRequested }) {
  const [busy, setBusy] = useState(false);
  const request = async () => {
    setBusy(true);
    try {
      await audienceExtractsApi.requestRebuild();
      toast.success('Rebuild requested. The ops dashboard will rebuild the audiences shortly.');
      onRequested();
    } catch (err) {
      toast.error(err?.message || 'Could not request a rebuild');
    } finally {
      setBusy(false);
    }
  };
  const pending = status?.building || status?.rebuild_requested_at;
  return (
    <Card data-testid="rebuild-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Rebuild (admins)</CardTitle>
        <CardDescription>
          The audiences are rebuilt monthly on the ops dashboard. Ask for an early rebuild here.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-sm space-y-1">
          {status?.building ? (
            <p>Building now.</p>
          ) : status?.rebuild_requested_at ? (
            <p>Rebuild requested {fmtDateTime(status.rebuild_requested_at)}; waiting for the dashboard.</p>
          ) : (
            <p className="text-muted-foreground">No rebuild pending.</p>
          )}
          {status?.last_error && (
            <p className="text-destructive" data-testid="rebuild-error">
              Last rebuild failed: {status.last_error}
            </p>
          )}
        </div>
        <Button onClick={request} disabled={busy || !!pending} data-testid="request-rebuild">
          <RefreshCw className={`h-4 w-4 mr-1 ${busy ? 'animate-spin' : ''}`} />
          Request rebuild
        </Button>
      </CardContent>
    </Card>
  );
}

export default function AudienceExtracts() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [region, setRegion] = useState('all');
  const [office, setOffice] = useState('all');
  const [entity, setEntity] = useState('all');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState(null);
  const [areasError, setAreasError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await audienceExtractsApi.list({ region, office, entity }));
      setError(null);
    } catch (err) {
      setError(err?.message || 'Could not load the audiences');
    } finally {
      setLoading(false);
    }
  }, [region, office, entity]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    audienceExtractsApi.areas()
      .then((d) => { if (!cancelled) setAreas(d); })
      .catch((err) => { if (!cancelled) setAreasError(err?.message || 'Could not load the areas'); });
    return () => { cancelled = true; };
  }, []);

  const regions = data?.filters?.regions || [];
  const entities = data?.filters?.entities || [];
  // Office options follow the chosen region AND side.
  const offices = useMemo(() => {
    const all = data?.filters?.offices || [];
    return all.filter((o) => (region === 'all' || o.region === region)
      && (entity === 'all' || o.entity === entity));
  }, [data, region, entity]);

  const grouped = useMemo(() => {
    const out = Object.fromEntries(GROUPS.map((g) => [g.id, []]));
    for (const a of data?.audiences || []) out[groupOf(a)].push(a);
    for (const list of Object.values(out)) list.sort((x, y) => (x.priority ?? 99) - (y.priority ?? 99));
    return out;
  }, [data]);

  const build = data?.build;
  const filters = { region, office, entity };
  const scope = data?.scope;
  const noScope = scope === 'none';

  return (
    <div className="space-y-6" data-testid="audience-extracts-page">
      <PageHeader
        title="Audience extracts"
        description="Approved customer-match audiences from the ops dashboard, for Meta and Google."
      />

      <div
        className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
        role="note"
        data-testid="audience-notice"
      >
        <p className="flex gap-2 font-medium">
          <ShieldAlert className="h-5 w-5 shrink-0" />
          Approved for online lookalike and retargeting audiences on Meta and Google only. No email,
          SMS, WhatsApp or calls. Every file is hashed; delete your copy once it is uploaded. Replace
          last month&apos;s audiences on each platform after every monthly rebuild so people who have
          unsubscribed drop out.
        </p>
        {build && (
          <div className="mt-3 space-y-1 pl-7" data-testid="build-info">
            <p>
              Built {fmtDate(build.built_at)}
              {build.data_through && Object.entries(build.data_through).map(([k, v]) => (
                <span key={k}> · {humanKey(k)} data to {fmtDate(v)}</span>
              ))}
            </p>
            {build.decision && <p className="italic">{build.decision}</p>}
          </div>
        )}
      </div>

      {scope && (
        <p className="flex items-center gap-2 text-sm" data-testid="audience-scope">
          <Eye className="h-4 w-4 text-muted-foreground" />
          You see: <span className="font-medium">{scopeLabel(scope)}</span>
        </p>
      )}

      {noScope && (
        <Card data-testid="audience-no-scope">
          <CardContent className="py-6 text-sm space-y-1">
            <p className="font-medium">Your account has no region or office assigned.</p>
            <p className="text-muted-foreground">
              Audience extracts show only your own regions or offices. Ask an administrator to set
              your region or office in User Management (Access Restrictions), or to grant you national
              access.
            </p>
          </CardContent>
        </Card>
      )}

      {error && !data && (
        <Card data-testid="audience-error">
          <CardContent className="py-6 text-sm">{error}</CardContent>
        </Card>
      )}

      {isAdmin && (data || error) && (
        <RebuildPanel status={data?.status} onRequested={load} />
      )}

      {data && !noScope && (
        <>
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="space-y-1 w-full md:w-64">
              <Label htmlFor="aud-region">Region</Label>
              <Select
                value={region}
                onValueChange={(v) => { setRegion(v); setOffice('all'); }}
              >
                <SelectTrigger id="aud-region" data-testid="region-select">
                  <SelectValue placeholder="All regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 w-full md:w-64">
              <Label htmlFor="aud-office">Office</Label>
              <Select value={office} onValueChange={setOffice}>
                <SelectTrigger id="aud-office" data-testid="office-select">
                  <SelectValue placeholder="All offices" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All offices</SelectItem>
                  {offices.map((o) => <SelectItem key={o.name} value={o.name}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 w-full md:w-48">
              <Label htmlFor="aud-entity">Branch / Franchise</Label>
              <Select
                value={entity}
                onValueChange={(v) => { setEntity(v); setOffice('all'); }}
              >
                <SelectTrigger id="aud-entity" data-testid="entity-select">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {entities.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {loading && <span className="text-sm text-muted-foreground">Updating counts…</span>}
          </div>

          {GROUPS.map((g) => {
            const list = grouped[g.id];
            if (!list.length) return null;
            const Icon = g.icon;
            return (
              <section key={g.id} className="space-y-3" data-testid={`group-${g.id}`}>
                <div className={g.id === 'reserve' ? 'opacity-70' : undefined}>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Icon className="h-5 w-5" /> {g.title}
                  </h2>
                  <p className="text-sm text-muted-foreground">{g.hint}</p>
                </div>
                {list.map((a) => (
                  <AudienceCard key={a.key} audience={a} filters={filters} reserve={g.id === 'reserve'} />
                ))}
              </section>
            );
          })}
        </>
      )}

      {!noScope && (
        <section className="space-y-3" data-testid="areas-section">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <MapPin className="h-5 w-5" /> High-value areas
            </h2>
            <p className="text-sm text-muted-foreground">
              For geo-targeting. Areas only: no personal data.
            </p>
          </div>
          {areasError && <p className="text-sm text-muted-foreground">{areasError}</p>}
          {areas?.note && <p className="text-sm text-muted-foreground">{areas.note}</p>}
          {areas?.areas?.length > 0 && <AreasTable areas={areas.areas} buildId={areas?.build?.id} />}
        </section>
      )}
    </div>
  );
}
